import React, { Dispatch } from "react";
import { useFormContext } from "react-hook-form";
import { UserLoginErrorCode } from "common-client/generated/graphql";
import { MFAFlowAction } from "common-client/utils/useMFAFlow";
import {
  useResetTotpSetupMutation,
  useVerifyAdminTotpMutation,
} from "../../../generated/graphql";
import { ProvideCode } from "../CommonLoginComponents/ProvideCode";
import { LoginFormStructure } from "../LoginPage";
import { isValidTotpCode, verifyLoginData } from "./utils";

export const ResetFlowCode = ({
  dispatch,
  error,
}: {
  dispatch: Dispatch<MFAFlowAction>;
  error: Maybe<{ message: string; code?: UserLoginErrorCode }>;
}) => {
  const { watch } = useFormContext();
  const code = watch("code");

  const [verifyTotpCode, { loading }] = useVerifyAdminTotpMutation();
  const [resetTotpSetup, { loading: resetLoading }] =
    useResetTotpSetupMutation();

  const onSubmit = async (loginData: LoginFormStructure) => {
    if (!verifyLoginData(loginData, dispatch)) {
      return;
    }

    const { data } = await verifyTotpCode({
      variables: {
        data: {
          email: loginData.email,
          password: loginData.password,
          totpToken: loginData.code,
        },
      },
    });

    if (data?.verifyAdminTotp?.ok) {
      await resetTotpSetup({
        variables: {
          email: loginData.email,
          password: loginData.password,
        },
        onCompleted: data => {
          if (data.resetTotpSetup?.ok) {
            dispatch({
              type: "setupTotp",
              data: data.resetTotpSetup,
            });
          } else {
            dispatch({
              type: "resetFlowTotpCode",
              data: {
                ok: false,
                error: {
                  message: "Failed to reset TOTP setup",
                  code: UserLoginErrorCode.INVALID_TOTP_SETUP,
                },
              },
            });
          }
        },
      });
    } else {
      dispatch({
        type: "resetFlowTotpCode",
        data: data?.verifyAdminTotp,
      });
    }
  };

  const disabled = !isValidTotpCode(code) || loading || resetLoading;

  return (
    <ProvideCode
      title="Reset your authenticator setup"
      subtitle="This will generate a new QR code and require you to re-set up your authenticator app. To continue, enter the 6-digit code from your current authenticator app."
      secondaryActions={[]}
      onSubmit={onSubmit}
      onBack={() => dispatch({ type: "verifyTotpCode" })}
      disabled={disabled}
      error={error}
    />
  );
};
